import {
	IconClock,
	IconMail,
	IconShieldLock,
	IconTrash,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { DisposableHomeHero } from "@/components/custom/disposable-home-hero";
import { FeedbackCard } from "@/components/feedback/card";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { config } from "@/data/config";
import type { DisposableStats } from "@/data/custom/disposable";
import { cn } from "@/lib/utils";
import {
	getStaggeredAnimationDelayStyle,
	MARKETING_GRID_CARD_CLASSES,
	MARKETING_GRID_CARD_OVERLAY_CLASSES,
	MARKETING_SECTION_ANIM_BASE,
} from "@/utils/animation";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { seo } from "@/utils/seo";

const features = [
	{
		title: "Privacy-first, link-first",
		description:
			"No account wall: the inbox is the URL you keep. Inbound only—I never send on your behalf, and I built this to receive mail, not to profile you.",
		icon: IconShieldLock,
	},
	{
		title: "Inboxes that actually go away",
		description:
			"You choose the lifetime. When the clock hits zero, the UI stops pretending that mail is still there—honest throwaway behavior, not marketing fine print.",
		icon: IconClock,
	},
	{
		title: "Bodies in, attachments out",
		description:
			"I keep text and HTML so you can read codes and receipts. Binaries never stick around—less surface area, fewer surprises.",
		icon: IconTrash,
	},
];

export const Route = createFileRoute("/_marketing/")({
	component: App,
	head: () =>
		seo({
			title: "Disposable email",
			description: `${config.siteName} — inbound-only disposable inboxes with honest expiry. Solo-built for trials, OTPs, and sign-ups without the SaaS theater.`,
			keywords:
				"disposable email, temporary email, privacy, throwaway, inbound only, self-destruct",
			type: "SoftwareApplication",
		}),
});

export function App() {
	const [reportOpen, setReportOpen] = useState(false);
	const [reportLocal, setReportLocal] = useState("");
	const [reportReason, setReportReason] = useState("");
	const [reportEmail, setReportEmail] = useState("");

	const statsQ = useQuery({
		queryKey: ["disposable-stats"],
		queryFn: async () => {
			const res = await requestWithHandling(() =>
				apiClient.api.disposableStatsList(),
			);
			if (res.code !== "SUCCESS" || res.data === undefined) {
				throw new TypedClientApiError(
					res.message || res.code || "Request failed",
					undefined,
					null,
					res.code,
				);
			}
			return res.data as unknown as DisposableStats;
		},
		staleTime: 30_000,
	});

	const onReport = async () => {
		try {
			const res = await requestWithHandling(() =>
				apiClient.api.disposableReportCreate({
					local_part: reportLocal,
					reason: reportReason,
					...(reportEmail.trim() !== ""
						? { contact_email: reportEmail.trim() }
						: {}),
				}),
			);
			if (res.code !== "SUCCESS") {
				throw new TypedClientApiError(
					res.message || res.code || "report_failed",
					undefined,
					null,
					res.code,
				);
			}
			toast.success("Report sent");
			setReportOpen(false);
			setReportLocal("");
			setReportReason("");
			setReportEmail("");
		} catch {
			toast.error("Could not send report");
		}
	};

	const aliases = statsQ.data?.aliases_created ?? "—";
	const messages = statsQ.data?.messages_received ?? "—";
	const domainsOffered =
		statsQ.data?.domains_offered !== undefined
			? statsQ.data.domains_offered
			: "—";

	const statsItems = [
		{ label: "Addresses created", value: aliases },
		{ label: "Messages received", value: messages },
		{ label: "Domains offered", value: domainsOffered },
	] as const;

	return (
		<MarketingShell>
			<section className="pb-16 pt-20 sm:pb-24 sm:pt-28">
				<MarketingContainer>
					<div
						className={cn(
							"mx-auto flex w-full max-w-5xl flex-col gap-12 lg:max-w-6xl lg:gap-16",
							MARKETING_SECTION_ANIM_BASE,
						)}
					>
						<MarketingHeader
							align="center"
							className="mb-0 sm:mb-0"
							title="The disposable inbox"
							titleIcon={IconMail}
							heading="Email that shows up and disappears on your terms."
							description="Spin up an inbound-only address in one breath: pick a domain, set a timer, copy the address or open the inbox."
							actions={
								<Fragment>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="text-muted-foreground"
										onClick={() => setReportOpen(true)}
									>
										Report abuse
									</Button>
									<Dialog open={reportOpen} onOpenChange={setReportOpen}>
										<DialogContent>
											<DialogHeader>
												<DialogTitle>
													Report abuse or request takedown
												</DialogTitle>
												<DialogDescription>
													Include the mailbox local-part and what is wrong. I
													triage reports when I can—this is spare-time
													infrastructure.
												</DialogDescription>
											</DialogHeader>
											<div className="grid gap-3 py-2">
												<div className="grid gap-2">
													<Label htmlFor="rp-local">
														Local part (before @)
													</Label>
													<Input
														id="rp-local"
														value={reportLocal}
														onChange={(e) => setReportLocal(e.target.value)}
														placeholder="e.g. suspicious-alias"
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="rp-reason">Details</Label>
													<Input
														id="rp-reason"
														value={reportReason}
														onChange={(e) => setReportReason(e.target.value)}
														placeholder="What should I know?"
													/>
												</div>
												<div className="grid gap-2">
													<Label htmlFor="rp-email">
														Your email (optional)
													</Label>
													<Input
														id="rp-email"
														type="email"
														value={reportEmail}
														onChange={(e) => setReportEmail(e.target.value)}
														placeholder="for follow-up"
													/>
												</div>
											</div>
											<DialogFooter>
												<Button
													variant="outline"
													onClick={() => setReportOpen(false)}
												>
													Cancel
												</Button>
												<Button onClick={() => void onReport()}>Submit</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>
								</Fragment>
							}
						/>
						<p className="mx-auto -mt-6 text-center text-sm text-muted-foreground sm:text-base">
							Powered by{" "}
							<a
								href="https://github.com/baker/inboundparse"
								target="_blank"
								rel="noreferrer"
								className="font-medium text-primary underline-offset-4 hover:underline"
							>
								InboundParse
							</a>{" "}
							for SMTP ingest.
						</p>
						<div
							className={cn(
								"w-full [animation-delay:240ms]",
								MARKETING_SECTION_ANIM_BASE,
							)}
						>
							<DisposableHomeHero />
						</div>
					</div>
				</MarketingContainer>
			</section>

			<section className="border-y border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<div className="rounded-2xl border border-border/60 bg-card/25 px-6 py-10 ring-1 ring-border/30 sm:px-10 sm:py-12">
						<div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-8">
							{statsItems.map((stat, index) => (
								<div
									key={stat.label}
									className={cn(
										"flex flex-col gap-1.5",
										MARKETING_SECTION_ANIM_BASE,
										index > 0 && "sm:border-l sm:border-border/50 sm:pl-8",
									)}
									style={getStaggeredAnimationDelayStyle(index, 0.08, 0.09)}
								>
									<p className="text-sm font-medium text-muted-foreground">
										{stat.label}
									</p>
									<p className="text-2xl font-semibold tracking-tight text-foreground tabular-nums sm:text-3xl">
										{stat.value}
									</p>
								</div>
							))}
						</div>
					</div>
				</MarketingContainer>
			</section>

			<section className="py-20 sm:py-24">
				<MarketingContainer>
					<MarketingHeader
						align="left"
						headingAs="h2"
						title="How it works"
						heading={'Built for the "prove you have an inbox" moment'}
						description="Trials, OTPs, sketchy vendors—when you need an address that is real enough to receive, but disposable enough to walk away from."
						className="mb-12 sm:mb-16"
					/>
					<dl className="grid gap-8 sm:gap-8 lg:grid-cols-3">
						{features.map((feature, index) => {
							const Icon = feature.icon;
							return (
								<div
									key={feature.title}
									className={cn(
										MARKETING_GRID_CARD_CLASSES,
										"relative p-6 sm:p-8",
									)}
									style={getStaggeredAnimationDelayStyle(index, 0.12, 0.1)}
								>
									<div
										className={MARKETING_GRID_CARD_OVERLAY_CLASSES}
										aria-hidden
									/>
									<div className="relative flex flex-col gap-4">
										<div className="flex size-11 items-center justify-center rounded-lg border border-border/60 bg-muted/50">
											<Icon className="size-5 text-primary" aria-hidden />
										</div>
										<dt className="text-lg font-semibold tracking-tight text-foreground">
											{feature.title}
										</dt>
										<dd className="m-0 text-base text-pretty text-muted-foreground">
											{feature.description}
										</dd>
									</div>
								</div>
							);
						})}
					</dl>
				</MarketingContainer>
			</section>

			<section className="border-t border-border/50 py-12 sm:py-14">
				<MarketingContainer>
					<div
						className={cn(
							"flex flex-col items-center gap-4 text-center",
							MARKETING_SECTION_ANIM_BASE,
						)}
					>
						<p className="text-sm font-medium text-muted-foreground">
							Switching from another disposable inbox?
						</p>
						<div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm">
							{(
								[
									{ href: "/alternatives/mailinator", label: "Mailinator" },
									{
										href: "/alternatives/guerrilla-mail",
										label: "Guerrilla Mail",
									},
									{ href: "/alternatives/temp-mail", label: "Temp Mail" },
									{
										href: "/alternatives/10minutemail",
										label: "10MinuteMail",
									},
								] as const
							).map(({ href, label }) => (
								<a
									key={href}
									href={href}
									className="text-primary underline-offset-4 hover:underline"
								>
									{label} alternatives
								</a>
							))}
						</div>
					</div>
				</MarketingContainer>
			</section>

			<section className="border-t border-border/50 py-20 sm:py-24">
				<MarketingContainer>
					<div className={cn(MARKETING_SECTION_ANIM_BASE, "delay-150")}>
						<FeedbackCard
							className="mx-auto max-w-2xl rounded-2xl ring-1 ring-border/30"
							title="Feedback"
							description="This is a solo side project. Send bugs, weird edge cases, ideas, or a deliverability rant—I read what comes in. Add your email if you want a reply; I will not use it for marketing."
						/>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
